import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  MapPin,
  MoonStar,
  Target,
  MessageCircle,
  Megaphone,
  Search,
  Instagram,
  Mail,
  Route,
  Globe,
  Clapperboard,
  Check,
  Layers,
  TrendingUp,
} from "lucide-react";
import { useSEO } from "../components/SEO";
import { Reveal, SectionTag, MaskedReveal } from "../components/Reveal";
import { BookCallButton, BlueprintButton, ArrowLink } from "../components/CTA";
import { Marquee } from "../components/Marquee";
import { WhatsAppFlow } from "../components/WhatsAppFlow";
import { FunnelVisual } from "../components/FunnelVisual";
import { PricingCards } from "../components/PricingCards";
import { CaseStudyGrid } from "../components/CaseStudyPlaceholder";
import { BlueprintSection } from "../components/BlueprintSection";
import { ComparisonSection } from "../components/ComparisonSection";
import { CtaBand } from "../components/CtaBand";
import { IMAGES } from "../config/images";

const TRUST = [
  { icon: MapPin, title: "India-Focused", text: "Built around the Indian customer journey." },
  { icon: MoonStar, title: "Hajj & Umrah Specialized", text: "Not a generic marketing agency." },
  { icon: Target, title: "Lead Generation Focused", text: "Designed around qualified enquiries and conversations." },
  { icon: MessageCircle, title: "WhatsApp-First Customer Journey", text: "Built around how many Indian customers actually communicate with travel agencies." },
];

const PROBLEMS = [
  "Inconsistent enquiry volume",
  "Overdependence on referrals",
  "Poorly optimized Facebook and Instagram advertising",
  "Leads that do not respond",
  "Enquiries that get lost in WhatsApp",
  "Weak follow-up systems",
  "Seasonal demand spikes",
  "Strong competition between travel operators",
  "Difficulty turning social media attention into genuine enquiries",
];

const SERVICES = [
  {
    icon: Megaphone,
    title: "Meta Ads",
    desc: "Generate targeted enquiries through Facebook and Instagram campaigns.",
    includes: ["Audience strategy", "Campaign setup", "Creative direction", "Lead generation", "Retargeting", "Campaign optimization"],
    cta: "Explore Meta Ads",
    to: "/services/meta-ads",
  },
  {
    icon: Search,
    title: "Google Ads",
    desc: "Capture high-intent Indian customers actively searching for Hajj and Umrah travel options.",
    includes: ["High-intent keywords", "Flexible geographic targeting", "Landing-page recommendations", "Conversion tracking"],
    cta: "Explore Google Ads",
    to: "/services/google-ads",
  },
  {
    icon: Instagram,
    title: "Social Media Marketing",
    desc: "Build a professional digital presence that increases trust before the first enquiry.",
    includes: ["Package communication", "Educational content", "FAQs", "Agency credibility", "Seasonal campaigns"],
    cta: "Explore Social Media",
    to: "/services/social-media-marketing",
  },
  {
    icon: MessageCircle,
    title: "WhatsApp Marketing",
    desc: "Turn enquiries into conversations: structured flows, follow-up and re-engagement.",
    includes: ["Enquiry flows", "Lead follow-up", "Customer segmentation", "Response structure", "Re-engagement"],
    cta: "Explore WhatsApp Marketing",
    to: "/services/whatsapp-marketing",
  },
  {
    icon: Mail,
    title: "Email Marketing",
    desc: "Nurture prospects who are not ready to book immediately.",
    includes: ["Lead nurturing", "Package updates", "Educational sequences", "Reminder campaigns", "Re-engagement"],
    cta: "Explore Email Marketing",
    to: "/services/email-marketing",
  },
  {
    icon: Globe,
    title: "Website Development",
    desc: "A professional, mobile-responsive website structured to build trust and turn visitors into enquiries.",
    includes: ["Homepage & core pages", "Hajj & Umrah package pages", "Enquiry form", "WhatsApp CTA", "SEO foundations", "Analytics setup"],
    cta: "Explore Website Development",
    to: "/services/website-development",
  },
  {
    icon: Clapperboard,
    title: "On-Ground Content Production",
    desc: "One professional content production day that gives your agency 10 polished reels ready for marketing.",
    includes: ["10 reels", "Content planning", "Production direction", "Editing & delivery", "Meta Ads-ready formats"],
    cta: "Explore Content Production",
    to: "/services/on-ground-content-production",
  },
  {
    icon: Route,
    title: "Hajj & Umrah Campaign Strategy",
    desc: "An integrated acquisition strategy built around your audience, offer, season and sales process.",
    includes: ["Audience & offer", "Creative", "Seasonality", "Channels", "Landing pages", "WhatsApp & follow-up", "Retargeting"],
    cta: "Explore Our Strategy",
    to: "/services/campaign-strategy",
    wide: true,
  },
];

const WHY = [
  { icon: MoonStar, title: "Hajj & Umrah Specialization", text: "Marketing designed around the pilgrimage travel customer journey." },
  { icon: MapPin, title: "India-Focused", text: "Strategy built around India's geography, customer behavior and communication habits." },
  { icon: Layers, title: "Full-Funnel Thinking", text: "We don't stop at generating a click or form submission." },
  { icon: TrendingUp, title: "Performance-Focused", text: "Campaign decisions should be guided by measurable business outcomes." },
];

const INFLUENCES = [
  "Campaign strategy", "Audience targeting", "Creative", "Messaging",
  "Lead generation", "WhatsApp follow-up", "Retargeting",
  "Customer journey", "Conversion strategy",
];

const BUYING_BEHAVIOUR = [
  "WhatsApp conversations",
  "Family decision-making",
  "Trust before contact",
  "Price and package comparison",
  "Local reputation",
  "Regional markets",
  "Referral influence",
  "Fast response expectations",
  "Mobile-first behavior",
];

const COMPARE_CHANNELS = ["Instagram", "Facebook", "Google", "WhatsApp", "Reviews", "Referrals", "Agency websites"];

const METRICS = [
  "Qualified enquiries", "Lead volume", "Cost per lead", "Lead quality",
  "WhatsApp conversations", "Consultations", "Booking opportunities",
];

export default function Home() {
  useSEO(
    "Hajj & Umrah Marketing Agency in India | Nexus Catalyst",
    "Nexus Catalyst helps Hajj & Umrah travel agencies across India generate qualified enquiries through specialized digital marketing, paid advertising, WhatsApp and email marketing."
  );
  const heroRef = useRef(null);
  const marketRef = useRef(null);
  const { scrollYProgress: heroProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(heroProgress, [0, 1], [0, 110]);
  const { scrollYProgress: marketProgress } = useScroll({ target: marketRef, offset: ["start end", "end start"] });
  const marketY = useTransform(marketProgress, [0, 1], [-40, 40]);

  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative bg-cream overflow-hidden" data-testid="home-hero">
        <div className="absolute inset-0 geo-pattern opacity-[0.05]" />
        <div className="absolute -top-32 -left-32 w-[480px] h-[480px] rounded-full bg-tan/10 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-6 pt-36 pb-20 md:pt-48 md:pb-28 grid lg:grid-cols-12 gap-14 lg:gap-10 items-center">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <SectionTag className="mb-8">Specialized Hajj &amp; Umrah Growth Marketing</SectionTag>
            </motion.div>
            <h1 className="text-[42px] sm:text-6xl lg:text-7xl font-bold tracking-tighter leading-[1.02] text-charcoal">
              <MaskedReveal
                lines={[
                  "Turn Hajj & Umrah",
                  "demand into a",
                  <>predictable <span className="text-tan">growth system.</span></>,
                ]}
              />
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
              className="mt-7 max-w-xl text-base md:text-lg text-charcoal/65 leading-relaxed"
              data-testid="hero-subcopy"
            >
              Nexus Catalyst helps Hajj &amp; Umrah travel agencies across India
              generate qualified enquiries and customer conversations through
              specialized digital marketing, paid advertising, WhatsApp and
              email marketing.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="mt-9 flex flex-col sm:flex-row gap-4"
            >
              <BookCallButton testid="hero-book-call-button" />
              <BlueprintButton testid="hero-growth-audit-button" />
            </motion.div>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.95 }}
              className="mt-8 flex items-center gap-3"
              data-testid="hero-supporting-statement"
            >
              <span className="h-1.5 w-1.5 rotate-45 bg-gold" />
              <span className="text-xs font-semibold tracking-[0.2em] uppercase text-charcoal/50">
                Built specifically for India's Hajj &amp; Umrah travel market.
              </span>
            </motion.div>
          </div>
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-5 relative"
          >
            <div className="absolute -top-5 -left-5 w-full h-full bg-tan/15" />
            <div className="absolute -bottom-5 -right-5 w-full h-full border border-gold/50" />
            <div className="relative overflow-hidden">
              <motion.img
                src={IMAGES.architectureTall}
                alt="Grand mosque architecture, the destination at the heart of every Hajj and Umrah journey"
                style={{ y: imgY }}
                className="w-full h-[380px] md:h-[520px] object-cover scale-110"
                data-testid="hero-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-charcoal/30 via-transparent to-transparent" />
            </div>
            <div className="absolute bottom-6 -left-4 md:-left-8 bg-cream border border-gold/40 px-5 py-4 shadow-[0_16px_40px_rgba(45,42,39,0.15)]">
              <div className="flex items-center gap-3">
                <MessageCircle size={18} strokeWidth={1.5} className="text-tan" />
                <div>
                  <div className="text-xs font-bold text-charcoal">WhatsApp-First</div>
                  <div className="text-[11px] text-charcoal/55">Enquiry → Conversation → Booking</div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <Marquee />

      {/* TRUST STRIP */}
      <section className="bg-white py-20 md:py-28" data-testid="trust-strip">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal className="mb-14">
            <SectionTag className="mb-6">Why We Exist</SectionTag>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-charcoal max-w-3xl">
              Built for Hajj &amp; Umrah. Focused on Growth.
            </h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {TRUST.map((t, i) => (
              <Reveal key={t.title} delay={i * 0.08} className="h-full">
                <div
                  className="h-full bg-cream border border-charcoal/10 p-7 transition-colors duration-300 hover:border-gold/50"
                  data-testid={`trust-card-${i + 1}`}
                >
                  <t.icon size={22} strokeWidth={1.5} className="text-gold" />
                  <h3 className="mt-5 text-lg font-semibold tracking-tight text-charcoal">{t.title}</h3>
                  <p className="mt-2 text-sm text-charcoal/60 leading-relaxed">{t.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PROBLEM */}
      <section className="bg-cream py-24 md:py-32" data-testid="problem-section">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-28">
              <Reveal>
                <SectionTag className="mb-6">The Problem</SectionTag>
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-charcoal leading-[1.05]">
                  Your Agency Shouldn't Have to Depend on Referrals Alone.
                </h2>
                <p className="mt-6 text-base text-charcoal/65 leading-relaxed">
                  Speak with Hajj &amp; Umrah operators across India, from
                  Mumbai to Surat, Hyderabad to Lucknow, and the same growth
                  problems keep surfacing.
                </p>
              </Reveal>
            </div>
          </div>
          <div className="lg:col-span-7">
            <div>
              {PROBLEMS.map((p, i) => (
                <Reveal key={p} delay={i * 0.04}>
                  <div
                    className="flex items-baseline gap-6 py-5 border-t border-charcoal/10"
                    data-testid={`problem-item-${i + 1}`}
                  >
                    <span className="text-xs font-semibold tracking-[0.25em] text-gold shrink-0">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="text-lg md:text-xl font-semibold tracking-tight text-charcoal">
                      {p}
                    </span>
                  </div>
                </Reveal>
              ))}
              <Reveal delay={0.2}>
                <div className="mt-8 bg-tan px-7 py-6" data-testid="problem-resolution">
                  <p className="text-cream text-base md:text-lg font-semibold leading-relaxed">
                    Nexus Catalyst connects advertising, enquiry generation and
                    follow-up into one growth system.
                  </p>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <ComparisonSection />

      {/* SERVICES */}
      <section className="bg-white py-24 md:py-32" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal className="mb-16 max-w-3xl">
            <SectionTag className="mb-6">Services</SectionTag>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-charcoal leading-[1.05]">
              Marketing Built Around the Hajj &amp; Umrah Customer Journey.
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.map((s, i) => (
              <Reveal
                key={s.title}
                delay={i * 0.06}
                className={s.wide ? "lg:col-span-3 h-full" : "h-full"}
              >
                <div
                  className={`h-full bg-cream border border-charcoal/10 p-8 flex flex-col transition-colors duration-300 hover:border-gold/50 ${
                    s.wide ? "lg:flex-row lg:items-center lg:gap-14" : ""
                  }`}
                  data-testid={`service-card-${i + 1}`}
                >
                  <div className={s.wide ? "lg:max-w-sm shrink-0" : ""}>
                    <div className="flex items-center justify-between">
                      <s.icon size={22} strokeWidth={1.5} className="text-gold" />
                      <span className="text-xs font-semibold tracking-[0.25em] text-charcoal/30">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                    </div>
                    <h3 className="mt-6 text-xl font-semibold tracking-tight text-charcoal">{s.title}</h3>
                    <p className="mt-3 text-sm text-charcoal/60 leading-relaxed">{s.desc}</p>
                  </div>
                  <div className={s.wide ? "mt-6 lg:mt-0 lg:flex-1" : "mt-6 flex-1"}>
                    <div className="flex flex-wrap gap-2">
                      {s.includes.map((inc) => (
                        <span
                          key={inc}
                          className="border border-charcoal/15 bg-white px-3 py-1.5 text-[11px] font-medium text-charcoal/65"
                        >
                          {inc}
                        </span>
                      ))}
                    </div>
                    <ArrowLink
                      to={s.to}
                      testid={`service-cta-${i + 1}`}
                      className="mt-6"
                    >
                      {s.cta}
                    </ArrowLink>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHY NEXUS */}
      <section className="bg-charcoal py-24 md:py-32 relative overflow-hidden" data-testid="why-nexus-section">
        <div className="absolute inset-0 geo-pattern-light opacity-[0.05]" />
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-12 gap-14">
            <div className="lg:col-span-5">
              <div className="lg:sticky lg:top-28">
                <Reveal>
                  <SectionTag dark className="mb-6">Why Nexus Catalyst</SectionTag>
                  <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-cream leading-[1.05]">
                    Not Another Generic Marketing Agency.
                  </h2>
                  <p className="mt-6 text-base text-cream/60 leading-relaxed">
                    Most agencies sell the same marketing services to every
                    industry. Nexus Catalyst is built around one specific
                    market: Hajj &amp; Umrah travel.
                  </p>
                  <p className="mt-4 text-sm text-cream/45 leading-relaxed">
                    That specialization shapes everything we do:
                  </p>
                  <div className="mt-5 flex flex-wrap gap-2">
                    {INFLUENCES.map((x) => (
                      <span key={x} className="border border-cream/15 px-3 py-1.5 text-[11px] font-medium text-cream/60">
                        {x}
                      </span>
                    ))}
                  </div>
                </Reveal>
              </div>
            </div>
            <div className="lg:col-span-7 grid sm:grid-cols-2 gap-6">
              {WHY.map((w, i) => (
                <Reveal key={w.title} delay={i * 0.08} className="h-full">
                  <div
                    className="h-full bg-charcoal-light border border-cream/10 p-8 transition-colors duration-300 hover:border-gold/40"
                    data-testid={`why-card-${i + 1}`}
                  >
                    <w.icon size={22} strokeWidth={1.5} className="text-gold" />
                    <h3 className="mt-6 text-xl font-semibold tracking-tight text-cream">{w.title}</h3>
                    <p className="mt-3 text-sm text-cream/55 leading-relaxed">{w.text}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* INDIAN MARKET */}
      <section ref={marketRef} className="bg-cream py-24 md:py-32 overflow-hidden" data-testid="indian-market-section">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center">
          <Reveal className="relative order-2 lg:order-1">
            <div className="absolute -top-5 -right-5 w-full h-full border border-gold/50" />
            <div className="relative overflow-hidden">
              <motion.img
                src={IMAGES.architectureCourtyard}
                alt="White mosque courtyard - calm, clean and premium"
                style={{ y: marketY }}
                className="w-full h-[380px] md:h-[500px] object-cover scale-110"
                loading="lazy"
                data-testid="market-image"
              />
              <div className="absolute inset-0 bg-cream/10" />
            </div>
          </Reveal>
          <div className="order-1 lg:order-2">
            <Reveal>
              <SectionTag className="mb-6">The Indian Market</SectionTag>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-charcoal leading-[1.05]">
                Built for How Indian Hajj &amp; Umrah Customers Actually Buy.
              </h2>
              <p className="mt-6 text-base text-charcoal/65 leading-relaxed">
                From metro cities to regional markets across Maharashtra,
                Gujarat, Telangana, Kerala, Uttar Pradesh, West Bengal,
                Karnataka and Tamil Nadu, pilgrimage travel decisions follow a
                distinct pattern.
              </p>
            </Reveal>
            <div className="mt-8 grid sm:grid-cols-2 gap-x-8 gap-y-3">
              {BUYING_BEHAVIOUR.map((b, i) => (
                <Reveal key={b} delay={i * 0.04}>
                  <div className="flex items-center gap-3" data-testid={`behaviour-${i + 1}`}>
                    <Check size={15} strokeWidth={2} className="text-gold shrink-0" />
                    <span className="text-sm font-medium text-charcoal/75">{b}</span>
                  </div>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.15}>
              <p className="mt-8 text-xs font-semibold tracking-[0.2em] uppercase text-charcoal/45">
                Customers may compare agencies through
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                {COMPARE_CHANNELS.map((c) => (
                  <span key={c} className="border border-charcoal/15 bg-white px-3 py-1.5 text-[11px] font-medium text-charcoal/65">
                    {c}
                  </span>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* LEAD GENERATION */}
      <section className="bg-white py-24 md:py-32" data-testid="lead-gen-section">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-14 items-start">
          <Reveal className="lg:col-span-7">
            <SectionTag className="mb-6">Lead Generation</SectionTag>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-charcoal leading-[1.05]">
              More Qualified Conversations. Not Just More Clicks.
            </h2>
            <p className="mt-6 text-base text-charcoal/65 leading-relaxed max-w-2xl">
              Traffic is easy to buy. What matters is whether the right Indian
              families and pilgrims raise their hand, and whether your team
              gets a real conversation with them. We measure our work against
              what actually moves your agency forward.
            </p>
            <ArrowLink to="/contact" testid="lead-gen-cta" className="mt-8">
              Improve My Lead Generation
            </ArrowLink>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-5">
            <div className="bg-cream border border-charcoal/10 p-8">
              <div className="text-xs font-semibold tracking-[0.25em] uppercase text-tan mb-5">
                What We Optimize For
              </div>
              <div className="flex flex-wrap gap-2">
                {METRICS.map((m) => (
                  <span key={m} className="border border-gold/40 bg-white px-3 py-1.5 text-[11px] font-semibold text-charcoal/70">
                    {m}
                  </span>
                ))}
              </div>
              <p className="mt-6 text-xs text-charcoal/50 leading-relaxed border-t border-charcoal/10 pt-5" data-testid="lead-gen-disclaimer">
                Marketing performance varies based on market, offer, budget,
                seasonality, competition and the agency's sales process. We
                never promise guaranteed bookings.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* BLUEPRINT LEAD MAGNET */}
      <BlueprintSection />

      {/* RESOURCE HUB TEASER */}
      <section className="bg-cream py-24 md:py-32 border-t border-charcoal/10" data-testid="resources-teaser">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal className="mb-14 flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div>
              <SectionTag className="mb-6">Resource Hub</SectionTag>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-charcoal">
                Hajj &amp; Umrah Marketing, Explained Properly.
              </h2>
            </div>
            <ArrowLink to="/resources" testid="resources-teaser-link">
              Browse All Guides
            </ArrowLink>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { slug: "hajj-lead-generation-india", cluster: "Hajj Lead Generation", title: "How Hajj Agencies in India Can Generate More Qualified Leads" },
              { slug: "hajj-umrah-marketing-costs-india", cluster: "Marketing Costs", title: "Hajj & Umrah Marketing Costs in India: What Agencies Should Plan For" },
              { slug: "whatsapp-marketing-hajj-umrah-agencies", cluster: "WhatsApp Marketing", title: "WhatsApp Marketing for Hajj & Umrah Agencies: From Enquiry to Conversation" },
            ].map((a, i) => (
              <Reveal key={a.slug} delay={i * 0.08} className="h-full">
                <div
                  className="h-full bg-white border border-charcoal/10 p-7 flex flex-col transition-colors duration-300 hover:border-gold/50"
                  data-testid={`resources-teaser-${a.slug}`}
                >
                  <span className="text-[10px] font-semibold tracking-[0.2em] uppercase text-tan">
                    {a.cluster}
                  </span>
                  <h3 className="mt-3 text-lg font-semibold tracking-tight text-charcoal leading-snug flex-1">
                    {a.title}
                  </h3>
                  <ArrowLink to={`/resources/${a.slug}`} testid={`resources-teaser-link-${a.slug}`} className="mt-5">
                    Read the Guide
                  </ArrowLink>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <WhatsAppFlow />
      <FunnelVisual />

      {/* CASE STUDIES PREVIEW */}
      <section className="bg-cream py-24 md:py-32" data-testid="case-studies-preview">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal className="mb-14 flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div>
              <SectionTag className="mb-6">Case Studies</SectionTag>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-charcoal">
                Documented Campaigns. Coming Soon.
              </h2>
            </div>
            <ArrowLink to="/case-studies" testid="case-studies-link">
              View Case Studies
            </ArrowLink>
          </Reveal>
          <CaseStudyGrid />
        </div>
      </section>

      <PricingCards showHeading />
      <CtaBand />
    </main>
  );
}
